import { useMouseActions } from 'hooks/listeners/useMouseActions';
import { useMarkerVisibility } from 'hooks/map/useMarkerVisibility';
import { useWorldControls } from 'hooks/map/useWorldControls';
import { isWithinRefs } from 'libs/utils/ref/refUtils';
import { EWorldAction } from 'libs/utils/world/worldTypes';
import { MapMouseEvent } from 'mapbox-gl';
import { useContext, useRef } from 'react';
import { useContainComputation } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';

export const useMouseListener = (wrapperMapRef, controlsRef) => {
    const delta = 6;
    const startX = useRef<number>();
    const startY = useRef<number>();
    const mouseActions = useMouseActions();
    const ctxEdit = useContext(CtxEdit);
    const worldControls = useWorldControls(controlsRef);
    const markerVisibility = useMarkerVisibility();
    const containComputations = useContainComputation(
        ctxEdit.worldMarkers.filter(markerVisibility.isMarkerVisibleForSelectionInEdit),
        ctxEdit.mapRef
    );

    function detectAfterClick(e: MouseEvent) {
        const diffX = Math.abs(e.pageX - startX.current);
        const diffY = Math.abs(e.pageY - startY.current);

        if (diffX < delta && diffY < delta) {
            return true;
        } else {
            return false;
        }
    }

    function handleSingleClick(e: MapMouseEvent) {
        const { finalMarker, marker, marker2 } = containComputations.computations(e.originalEvent);
        const isCtrlDown = e.originalEvent.ctrlKey || e.originalEvent.metaKey;

        const clickedMarker = worldControls.getClickedMarker(e);
        if (clickedMarker) {
            mouseActions.forSingleClick(clickedMarker, isCtrlDown);
            return;
        }

        if (wrapperMapRef?.current?.contains(e.target) || finalMarker) {
            if (ctxEdit.worldAction === EWorldAction.SELECT && !e.originalEvent.shiftKey) {
                if (marker) {
                    mouseActions.forSingleClick(marker, isCtrlDown);
                } else if (marker2) {
                    mouseActions.forSingleClick(marker2, isCtrlDown);
                } else {
                    mouseActions.forSingleClick(null, isCtrlDown);
                }
            }
        }
        return;
    }

    function mouseDown(e: MapMouseEvent) {
        if (isWithinRefs(controlsRef, e)) return;

        // console.log('mouse down');
        const { finalMarker, marker, marker2 } = containComputations.computations(e.originalEvent);

        if (wrapperMapRef?.current?.contains(event.target) || finalMarker) {
            if (ctxEdit.worldAction === EWorldAction.MULTISELECT) {
                mouseActions.forMultiSelect(e);
            } else {
                if (e.originalEvent.shiftKey) {
                    if (marker) {
                        mouseActions.forShiftSelect(marker);
                    } else if (marker2) {
                        mouseActions.forShiftSelect(marker2);
                    }
                } else {
                    startX.current = e.originalEvent.pageX;
                    startY.current = e.originalEvent.pageY;
                }
            }
        }
        return;
    }

    function mouseUp(e: MapMouseEvent) {
        // console.log('mouse up');
        if (detectAfterClick(e.originalEvent)) {
            handleSingleClick(e);
        }
    }

    return {
        mouseDown,
        mouseUp
    };
};
